import { Grid, Paper } from '@mui/material';
import { useSnackbar } from 'notistack';
import { useEffect, useState } from 'react';
import { SubmitHandler } from 'react-hook-form';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { useNavigate, useParams } from 'react-router';
import { EmailReportForm } from '.';
import * as emailReportApi from '../../api/app/emailReportApi';
import { ApiError } from '../../components/core/ApiError/ApiError';
import { Loader } from '../../components/core/Loader/Loader';
import { PageContent } from '../../components/core/PageContent/PageContent';
import { PageHeader } from '../../components/core/PageHeader/PageHeader';
import { useAuth } from '../../contexts/authWindows';
import { useTranslation } from '../../contexts/translation';
import { EmailReport, EmailReportInputs } from '../../types/app/emailReport.types';
import { UserRoles } from '../../types/core/auth.types';
import { convertDataFromInputToUpdateRequest } from '../../utils/core/functions/emailReport.functions';
import { paths } from '../../utils/core/routes';
import { EmailReportAssetList } from '../EmailReportAsset/EmailReportAssetList';
import { EmailReportUserList } from '../EmailReportUser/EmailReportUserList';
import { useStyles } from './EmailReportForm.styles';

export function EmailReportDetail() {
  const { t } = useTranslation();
  const classes = useStyles();
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const isReadOnly =
    user?.roles.includes(UserRoles.Admin) || user?.roles.includes(UserRoles.EmailReports)
      ? false
      : true;
  const { id } = useParams();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [defaultValues, setDefaultValues] = useState<EmailReportInputs | null>(null);
  const [showUnmarkButton, setShowUnmarkButton] = useState<boolean>(true);
  const { mutate: mutateUpdateEmailReport } = useMutation(emailReportApi.updateEmailReport);
  const { mutate: mutateUnMarkAsSent } = useMutation(emailReportApi.unMarkAsSent);
  const { data, isLoading, isError } = useQuery<EmailReport>(['emailReports', id], () =>
    emailReportApi.getEmailReport(Number(id)),
  );
  const [selectedTz, setSelectedTz] = useState<string>();
  useEffect(() => {
    if (!data) return;

    setDefaultValues({
      id: data.id,
      name: data.name,
      type: data.type,
      emailSubject: data.emailSubject,
      dateTimeFormat: data.dateTimeFormat,
      shiftId: data.shiftId,
      timeZone: data.timeZone,
      sendOnMonday: data.sendOnMonday,
      sendOnTuesday: data.sendOnTuesday,
      sendOnWednesday: data.sendOnWednesday,
      sendOnThursday: data.sendOnThursday,
      sendOnFriday: data.sendOnFriday,
      sendOnSaturday: data.sendOnSaturday,
      sendOnSunday: data.sendOnSunday,
      intParam1: data.intParam1,
      startOfWeek: data.startOfWeek,
    });
    data.lastSent === null ? setShowUnmarkButton(false) : setShowUnmarkButton(true);
  }, [data]);

  const handleOnSubmit: SubmitHandler<EmailReportInputs> = (data: EmailReportInputs) => {
    if (!defaultValues) return;

    const requestData = convertDataFromInputToUpdateRequest({
      ...data,
      id: Number(id!),
      intParam1: data.type !== 'HourlyPerformance' ? data.intParam1 : null,
    });

    mutateUpdateEmailReport(
      { ...requestData, id: Number(id) },
      {
        onSuccess: () => {
          enqueueSnackbar(t('page.email-report.item-edited'), { variant: 'success' });
          navigate(paths.EMAIL_REPORT_LIST);
        },
        onError: (result) => {
          enqueueSnackbar(`${result}`, { variant: 'error' });
        },
      },
    );
  };

  const handleUnMarkAsSend = () => {
    mutateUnMarkAsSent(Number(id), {
      onSuccess: () => {
        queryClient.invalidateQueries(['emailReports']);
        setShowUnmarkButton(false);
        enqueueSnackbar(t('page.email-report.edit-button-unmark'), { variant: 'success' });
      },
      onError: (result) => {
        enqueueSnackbar(`${result}`, { variant: 'error' });
      },
    });
  };

  if (isError) return <ApiError />;
  if (isLoading || !data || !defaultValues) return <Loader />;

  return (
    <>
      <PageHeader title={data.name} />
      <PageContent>
        <Grid container spacing={3}>
          <Grid item xs={12} md={12}>
            <Paper className={classes.paper}>
              <EmailReportForm
                onSubmit={handleOnSubmit}
                onBack={() => navigate(paths.EMAIL_REPORT_LIST)}
                isReadOnly={isReadOnly}
                defaultValues={defaultValues}
                handleUnMarkAsSend={handleUnMarkAsSend}
                showUnmarkButton={showUnmarkButton}
                setSelectedTz={setSelectedTz}
                selectedTz={selectedTz}
              />
            </Paper>
          </Grid>
          <Grid item xs={8}></Grid>
          <Grid item xs={12} md={6}>
            <EmailReportAssetList isReadOnly={isReadOnly} />
          </Grid>
          <Grid item xs={12} md={6}>
            <EmailReportUserList isReadOnly={isReadOnly} />
          </Grid>
        </Grid>
      </PageContent>
    </>
  );
}
